import { Descendant } from "slate";

/**
 * Parses plain text with @mentions into Slate nodes
 * @param text - Plain text string with mentions in format @{userId} (e.g., "Hello @1")
 * @returns Array of Slate nodes with parsed mentions
 */
export function simpleDeserialize(text: string, getSuggestionLabel: (value: string) => string): Descendant[] {
  if (!text || text.trim() === "") {
    return [{ type: "paragraph", children: [{ text: "" }] }];
  }

  // Split text by newlines to create multiple paragraphs
  const lines = text.split("\n");
  const paragraphs: Descendant[] = [];

  for (const line of lines) {
    // Regex to match @{number} patterns
    const mentionRegex = /@id(\d+)/g;
    const children: Descendant[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // Find all mentions and split text accordingly
    while ((match = mentionRegex.exec(line)) !== null) {
      const matchStart = match.index;
      const matchEnd = mentionRegex.lastIndex;
      const value = match[1]; // Extract user ID as string
      const label = getSuggestionLabel(value) || `[Unknown User ${value}]`;

      // Add text before the mention (preserving tabs and whitespace)
      if (matchStart > lastIndex) {
        const textBefore = line.substring(lastIndex, matchStart);
        children.push({ text: textBefore });
      }

      // Add the mention element (using 'id' field to match existing Mention component)
      children.push({
        type: "mention",
        id: value,
        label: label,
        children: [{ text: "" }],
      });

      lastIndex = matchEnd;
    }

    // Add remaining text after the last mention (preserving tabs and whitespace)
    if (lastIndex < line.length) {
      const textAfter = line.substring(lastIndex);
      children.push({ text: textAfter });
    }

    // If no children were added (empty line), add empty text node
    if (children.length === 0) {
      children.push({ text: "" });
    }

    // Add paragraph for this line
    paragraphs.push({
      type: "paragraph",
      children,
    });
  }

  return paragraphs;
}
